export const AUTH008 = 'AUTH008';
export const REDIRECTING_TO_SHOPIFY = 'Redirecting to Shopify…';
export const CONNECT_REAUTH_KEY = 'shopificConnectReauth';

// Long enough to return from Shopify and submit the form again. After this,
// or after the merchant backs out, recovery can run once more.
export const REAUTH_WINDOW_MS = 10 * 60 * 1000;

const SHOPIFY_HOST = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;
const AUTHORIZE_PATH = '/admin/oauth/authorize';

export const OPEN_INSTALL_ERROR =
  'Shopify returned an install link this app will not open.';

let reauthPromise = null;

// Canonical install-grant URL for this shop, or null. Navigate the return
// value only. The raw string can parse as that grant here and as a path on
// this page once the browser resolves it against the document.
export const isShopifyAuthorizeUrl = (callbackURL, shop) => {
  if (!callbackURL || typeof callbackURL !== 'string') return null;
  if (!shop || typeof shop !== 'string') return null;
  const shopHost = shop.toLowerCase();
  if (!SHOPIFY_HOST.test(shopHost)) return null;
  try {
    const url = new URL(callbackURL);
    const grant =
      url.protocol === 'https:' &&
      url.username === '' &&
      url.password === '' &&
      url.port === '' &&
      url.hostname === shopHost &&
      url.pathname === AUTHORIZE_PATH;
    return grant ? url.href : null;
  } catch (err) {
    return null;
  }
};

// A callback URL means Shopify is still installing. Configuring the app in
// that state mounts TenantProvider with no session.
export const callbackUrlGate = (callbackURL, shop) => {
  if (!callbackURL) return { action: 'configure' };
  const url = isShopifyAuthorizeUrl(callbackURL, shop);
  if (url) return { action: 'navigate', url };
  return { action: 'stop', message: OPEN_INSTALL_ERROR };
};

// SameSite=Lax cookies are not sent on XHR from inside the Shopify admin
// iframe. Break out so the later signup request is same-site.
export const navigateTopLevel = (url) => {
  if (window.top && window.top !== window) {
    window.top.location.href = url;
    return;
  }
  window.location.href = url;
};

const readReauth = () => {
  try {
    const raw = sessionStorage.getItem(CONNECT_REAUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed.shop !== 'string' ||
      typeof parsed.at !== 'number'
    ) {
      return null;
    }
    return parsed;
  } catch (err) {
    return null;
  }
};

const attemptIsCurrent = (shop, now) => {
  const record = readReauth();
  if (!record || record.shop !== shop) return false;
  return now - record.at < REAUTH_WINDOW_MS;
};

export const clearConnectReauth = () => {
  reauthPromise = null;
  sessionStorage.removeItem(CONNECT_REAUTH_KEY);
};

export const clearConnectReauthOnBackForward = () => {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) {
    return;
  }
  const [nav] = performance.getEntriesByType('navigation');
  if (nav && nav.type === 'back_forward') clearConnectReauth();
};

const auth008From = (error) => {
  if (!error) return null;
  if (error.code === AUTH008) return error;
  const list = Array.isArray(error.errors) ? error.errors : null;
  if (!list) return null;
  return list.find((item) => item && item.code === AUTH008) || null;
};

export const recoverConnectProof = async ({
  error,
  shop,
  checkInstallation,
  assignLocation,
  now = Date.now(),
}) => {
  const authError = auth008From(error);
  if (!authError || !shop) return { error };

  if (reauthPromise) return reauthPromise;
  if (attemptIsCurrent(shop, now)) return { error: authError };

  reauthPromise = (async () => {
    try {
      const installation = await checkInstallation(shop, { reauthorize: true });
      const callbackURL = installation && installation.callbackURL;
      const authorizeUrl = isShopifyAuthorizeUrl(callbackURL, shop);
      if (!authorizeUrl) {
        return { error: authError };
      }
      sessionStorage.setItem(
        CONNECT_REAUTH_KEY,
        JSON.stringify({ shop, at: now })
      );
      assignLocation(authorizeUrl);
      return { redirecting: true };
    } catch (reauthErr) {
      return {
        error: {
          code: authError.code,
          title: authError.title,
          message: authError.message || reauthErr.message,
        },
      };
    } finally {
      reauthPromise = null;
    }
  })();
  return reauthPromise;
};
